/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { apiService } from "network/api";
import { axiosService } from "network/axios";
import { calculateDueDate } from "network/helper";
import { clearProjectRequest, updateProjectStatus, saveDraftProjectId } from "store/customerProject/reducer";
import { uploadProjectSelector, draftProjectIdSelector } from "store/customerProject/selector";
import { userSelector } from "store/user/selector";
import { WizardStep } from "../types";
import { calculateOrderTotal } from "../constants/pricing";
import { areDraftSavesLocked, trackDraftSave, unlockDraftSaves } from "./checkoutPaymentGuard";

const getLS_STEP = (userId: string) => `order.step.${userId}`;
const getLS_MAX_STEP = (userId: string) => `order.maxStep.${userId}`;
const getLS_DRAFT = (userId: string) => `order.draft.${userId}`;
const getLS_DRAFT_PROJECT_ID = (userId: string) => `order.draftProjectId.${userId}`;

export function useOrder() {
  const dispatch = useDispatch();
  const { orderId } = useParams();

  const prevOrderIdRef = useRef<string | undefined>(undefined);
  const hydratedOrderIdRef = useRef<string | undefined>(undefined);
  const cameFromExistingOrderRef = useRef(false);
  const isSavingDraftRef = useRef(false);
  const justHydratedRef = useRef(false);

  const uploadProject = useSelector(uploadProjectSelector);
  const user = useSelector(userSelector as any) as any;
  const draftProjectId = useSelector(draftProjectIdSelector as any) as string | null;

  const [isDraftProject, setIsDraftProject] = useState(false);

  const [step, setStep] = useState<WizardStep>(() => {
    const userId = user?.user_id;
    if (!userId) return 1;
    const raw = Number(localStorage.getItem(getLS_STEP(userId)));
    if (raw === 1 || raw === 2 || raw === 3 || raw === 4) return raw as WizardStep;
    return 1;
  });

  const [maxStep, setMaxStep] = useState<WizardStep>(() => {
    const userId = user?.user_id;
    if (!userId) return 1;
    const raw = Number(localStorage.getItem(getLS_MAX_STEP(userId)));
    if (raw === 2 || raw === 3 || raw === 4) return raw as WizardStep;
    return 1;
  });

  const [isHydrating, setIsHydrating] = useState(false);

  const isExistingOrder = !!orderId;

  // A previous checkout may have locked draft saves after paying; a freshly
  // mounted wizard is a new session and must be able to save drafts again.
  useEffect(() => {
    unlockDraftSaves();
  }, []);

  const readPersistedStep = (): WizardStep => {
    try {
      if (!user?.user_id) return 1;
      const raw = Number(localStorage.getItem(getLS_STEP(user.user_id)));
      if (raw === 1 || raw === 2 || raw === 3 || raw === 4) return raw as WizardStep;
      return 1;
    } catch (e) {
      return 1;
    }
  };

  const readPersistedMaxStep = (): WizardStep => {
    try {
      if (!user?.user_id) return 1;
      const raw = Number(localStorage.getItem(getLS_MAX_STEP(user.user_id)));
      if (raw === 2 || raw === 3 || raw === 4) return raw as WizardStep;
      return 1;
    } catch (e) {
      return 1;
    }
  };

  useEffect(() => {
    // When switching back to /main (no orderId), restore wizard progress from localStorage.
    // When opening /main/:orderId, do NOT take step/maxStep from localStorage draft.
    if (!orderId) {
      setIsDraftProject(false);
      if (cameFromExistingOrderRef.current) {
        // After visiting /main/:orderId, prefer starting from step 1 when returning to /main.
        setStep(1);
        setMaxStep(readPersistedMaxStep());
        cameFromExistingOrderRef.current = false;
      } else {
        setStep(readPersistedStep());
        setMaxStep(readPersistedMaxStep());
      }
      return;
    }

    cameFromExistingOrderRef.current = true;
    setStep(1);
    setMaxStep(4);
  }, [orderId]);

  useEffect(() => {
    const prevOrderId = prevOrderIdRef.current;
    const nextOrderId = orderId;
    prevOrderIdRef.current = nextOrderId;

    // When the wizard is used without an orderId in URL (/customer/main),
    // we should not clear progress on every mount — it breaks persistence.
    // Clear only when we explicitly moved from an existing order to a new wizard.
    const movedFromOrderToNewWizard = !!prevOrderId && !nextOrderId;
    if (!movedFromOrderToNewWizard) return;

    if (!user?.user_id) return;

    try {
      const raw = localStorage.getItem(getLS_DRAFT(user.user_id));
      if (raw) return;
    } catch (e) {
    }

    localStorage.removeItem(getLS_STEP(user.user_id));
    localStorage.removeItem(getLS_MAX_STEP(user.user_id));
    dispatch(clearProjectRequest());
    setStep(1);
    setMaxStep(1);
  }, [dispatch, orderId]);

  useEffect(() => {
    if (!orderId) return;
    if (hydratedOrderIdRef.current === orderId) return;
    hydratedOrderIdRef.current = orderId;

    let cancelled = false;
    setIsHydrating(true);

    (async () => {
      try {
        const res = await apiService.getProjectDetailByOrderId(orderId);
        if (cancelled) return;

        const data: any = res?.data ?? null;
        const order: any = data?.order ?? data ?? {};
        const docsRaw: any[] = (data?.documents ?? order?.documents ?? []) as any[];

        // Check if this is a Draft project (orderStatus === 10)
        const orderStatus = Number(order?.order_status ?? order?.orderStatus ?? 0);
        const isDraftProject = orderStatus === 10;
        setIsDraftProject(isDraftProject);

        // Get saved wizard step from server for Draft projects
        const savedWizardStep = Number(order?.wizard_step ?? order?.wizardStep ?? 0);
        
        // For Draft projects, restore the saved step; for non-draft orders, allow full access
        if (isDraftProject && savedWizardStep >= 1 && savedWizardStep <= 4) {
          setStep(savedWizardStep as WizardStep);
          setMaxStep(savedWizardStep as WizardStep);
        } else if (isDraftProject) {
          // Draft with no saved step — start from step 1
          setStep(1);
          setMaxStep(1);
        } else {
          setMaxStep(4);
        }

        const serverTotalRaw =
          order?.total_price ??
          order?.totalPrice ??
          order?.order_total ??
          order?.orderTotal ??
          order?.grand_total ??
          order?.grandTotal ??
          order?.price ??
          order?.amount ??
          data?.total_price ??
          data?.totalPrice ??
          data?.order_total ??
          data?.orderTotal ??
          data?.grand_total ??
          data?.grandTotal ??
          data?.price ??
          data?.amount;
        const serverTotal = Number(serverTotalRaw || 0) || 0;

        const translationType = String(order?.translation_type ?? order?.transitionType ?? "certified");
        const translatedFrom = String(order?.translated_from ?? order?.sourceLanguage ?? "English");
        const translatedToRaw = String(order?.translated_to ?? "");
        const translatedTo = translatedToRaw
          ? translatedToRaw
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean)
          : [];

        const orderTotalPages = Number(order?.page_count ?? order?.pageCount ?? 0) || 0;
        const orderTotalWords = Number(order?.words_count ?? order?.wordsCount ?? 0) || 0;

        const mappedDocs = (docsRaw || []).map((d: any) => {
          const originFile = d?.origin_file ?? d?.originFile ?? {};
          const name =
            originFile?.document_original_file_name ??
            originFile?.documentOriginalFileName ??
            d?.document_original_file_name ??
            d?.documentOriginalFileName ??
            d?.file_name ??
            d?.fileName ??
            "";

          const pages = Number(
            d?.pages ?? d?.page_count ?? d?.pageCount ??
            originFile?.pages ?? originFile?.page_count ?? originFile?.pageCount ?? 0
          ) || 0;
          const words = Number(
            d?.words ?? d?.words_count ?? d?.wordsCount ?? d?.word_count ?? d?.wordCount ??
            originFile?.words ?? originFile?.words_count ?? originFile?.wordsCount ?? originFile?.word_count ?? originFile?.wordCount ?? 0
          ) || 0;

          const fileUrl = originFile?.document_file_path ?? originFile?.documentFilePath ?? originFile?.document_original_file_path ?? originFile?.documentOriginalFilePath ?? originFile?.file_path ?? "";

          return {
            id: d?.document_id ?? d?.documentId ?? d?.id,
            pages,
            words,
            type: "uploaded",
            fileUrl,
            file: { name },
            origin_file: {
              document_original_file_name: name,
              document_file_path: fileUrl,
            },
          };
        });

        // Merge per-doc words/pages from localStorage cache (backend doesn't persist per-doc counts)
        try {
          const cachedDocsRaw = localStorage.getItem(`order.docs.${orderId}`);
          if (cachedDocsRaw) {
            const cachedDocs: any[] = JSON.parse(cachedDocsRaw);
            const cacheMap = new Map(cachedDocs.map((cd: any) => [String(cd.id), cd]));
            mappedDocs.forEach((md: any) => {
              const cached = cacheMap.get(String(md.id));
              if (cached) {
                if (md.pages === 0 && Number(cached.pages) > 0) md.pages = Number(cached.pages);
                if (md.words === 0 && Number(cached.words) > 0) md.words = Number(cached.words);
              }
            });
          }
        } catch (e) { /* ignore */ }

        const addressRaw = String(order?.address_fields ?? order?.address ?? "");
        const addressParts = addressRaw.split(";").map((s) => s.trim());
        const mailedHardCopyRecipientName = addressParts[0] || "";
        const mailedHardCopyStreetAddress = addressParts[1] || "";
        const mailedHardCopyCity = addressParts[2] || "";
        const mailedHardCopyZipCode = addressParts[3] || "";
        const mailedHardCopyState = addressParts[4] || "";
        const mailedHardCopyCountry = addressParts[5] || "";

        const nextState = {
          orderId: order?.order_id ?? order?.orderId ?? orderId,
          projectTitle: String(order?.order_name ?? order?.projectTitle ?? ""),
          projectDescription: String(order?.project_description ?? order?.projectDescription ?? ""),
          sourceLanguage: translatedFrom,
          targetLanguage: translatedTo,
          transitionType: translationType === "standard" ? "standard" : "certified",
          include_expedited_turnaround: String(order?.include_expedited_turnaround ?? order?.includeExpeditedTurnaround ?? "0"),
          address: addressRaw,
          mailedHardCopyRecipientName,
          mailedHardCopyStreetAddress,
          mailedHardCopyCity,
          mailedHardCopyZipCode,
          mailedHardCopyState,
          mailedHardCopyCountry,
          isMailHardCopy:
            String(order?.include_mail_hard_copy ?? order?.includeMailHardCopy ?? "0") === "1" ||
            String(order?.include_2_day_shipping ?? order?.include2DayShipping ?? "0") === "1",
          include_desktop_publishing: String(order?.include_desktop_publishing ?? order?.includeDesktopPublishing ?? "0") === "1",
          include_proofreading: String(order?.include_proofreading ?? order?.includeProofreading ?? "0") === "1",
          isNotarized: String(order?.include_notarization ?? order?.includeNotarization ?? "0") === "1",
          text: String(order?.text ?? ""),
          documents: mappedDocs,
          totalPage: orderTotalPages,
          totalWords: orderTotalWords,
          serverTotal,
        };

        dispatch(clearProjectRequest());
        dispatch(updateProjectStatus(nextState));
        justHydratedRef.current = true;
        
        // Save draftProjectId to Redux if this is a draft
        if (isDraftProject) {
          dispatch(saveDraftProjectId(orderId));
        }
      } catch (e) {
        // ignore
      } finally {
        if (!cancelled) setIsHydrating(false);
      }
    })();

    return () => {
      cancelled = true;
      setIsHydrating(false);
    };
  }, [dispatch, orderId]);

  // Cache per-doc words/pages to localStorage (backend doesn't persist per-doc counts)
  useEffect(() => {
    const projectId = orderId || draftProjectId;
    if (!projectId) return;
    if (!uploadProject.documents?.length) return;
    try {
      const docsToCache = uploadProject.documents.map((d: any) => ({
        id: String(d.id),
        pages: d.pages || 0,
        words: d.words || 0,
      }));
      localStorage.setItem(`order.docs.${projectId}`, JSON.stringify(docsToCache));
    } catch (e) { /* ignore */ }
  }, [orderId, draftProjectId, uploadProject.documents]);

  // Restore draft data from localStorage on mount/page reload
  useEffect(() => {
    if (orderId) return;
    if (!user?.user_id) return;

    // Check if Redux state is empty (page reload scenario)
    const hasData = uploadProject.documents?.length > 0 || 
                    uploadProject.projectTitle || 
                    uploadProject.text ||
                    uploadProject.targetLanguage?.length > 0;
    
    if (hasData) return; // Data already in Redux, don't overwrite

    try {
      const raw = localStorage.getItem(getLS_DRAFT(user.user_id));
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return;
      dispatch(updateProjectStatus(parsed));
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [dispatch, orderId, user?.user_id]);

  useEffect(() => {
    if (orderId) return;
    if (!user?.user_id) return;

    try {
      localStorage.setItem(getLS_DRAFT(user.user_id), JSON.stringify(uploadProject));
    } catch (e) {
    }
  }, [orderId, uploadProject, user?.user_id]);

  // Save draft progress to server when step changes
  const saveDraftProgressToServer = useCallback(async (currentStep: WizardStep) => {
    const projectId = orderId || draftProjectId;
    if (!user?.user_id) return;
    if (!projectId) return;
    if (isSavingDraftRef.current) return;
    if (areDraftSavesLocked()) return;

    // For existing orders, always save changes regardless of data state
    // For new orders, check if there's meaningful data before saving
    if (!orderId) {
      const hasDocs = uploadProject.documents?.length > 0;
      const hasData = uploadProject.projectTitle || uploadProject.text || (uploadProject.targetLanguage?.length > 0);
      if (!hasDocs && !hasData) return;
    }

    try {
      isSavingDraftRef.current = true;

      const {
        mailedHardCopyRecipientName,
        mailedHardCopyStreetAddress,
        mailedHardCopyCity,
        mailedHardCopyZipCode,
        mailedHardCopyState,
        mailedHardCopyCountry,
        documents,
        serverTotal,
        ...restUploadProject
      } = uploadProject as any;

      const includeExpedited =
        (uploadProject as any)?.include_expedited_turnaround === true ||
        (uploadProject as any)?.include_expedited_turnaround === 1 ||
        (uploadProject as any)?.include_expedited_turnaround === "1";

      const translateSpeed = includeExpedited ? "express" : "standard";

      const formattedAddress = [
        mailedHardCopyRecipientName || "",
        mailedHardCopyStreetAddress || "",
        mailedHardCopyCity || "",
        mailedHardCopyZipCode || "",
        mailedHardCopyState || "",
        mailedHardCopyCountry || "",
      ].join("; ");

      const calculatedPrice = calculateOrderTotal({
        transitionType: uploadProject.transitionType,
        include_expedited_turnaround: (uploadProject as any).include_expedited_turnaround,
        totalPage: uploadProject.totalPage,
        totalWords: uploadProject.totalWords,
        targetLanguage: uploadProject.targetLanguage || [],
        isNotarized: uploadProject.isNotarized,
        isMailHardCopy: uploadProject.isMailHardCopy,
        include_desktop_publishing: (uploadProject as any).include_desktop_publishing,
        include_proofreading: (uploadProject as any).include_proofreading,
      });

      // Compute actual totals from documents to avoid sending stale 0 values
      const docsPages = (uploadProject.documents || []).reduce((s: number, d: any) => s + (Number(d.pages) || 0), 0);
      const docsWords = (uploadProject.documents || []).reduce((s: number, d: any) => s + (Number(d.words) || 0), 0);
      const safeTotalPage = uploadProject.totalPage || docsPages;
      const safeTotalWords = uploadProject.totalWords || docsWords;

      // Send documents with IDs, counts, file name, fileUrl, and origin_file
      const safeDocuments = (uploadProject.documents || []).map((d: any) => ({
        id: d.id,
        document_id: d.id,
        pages: d.pages || 0,
        words: d.words || 0,
        type: d.type,
        ...(d.file?.name ? { file: { name: d.file.name } } : {}),
        ...(d.fileUrl ? { fileUrl: d.fileUrl } : {}),
        ...(d.origin_file ? { origin_file: d.origin_file } : {}),
      }));

      const payload = {
        ...restUploadProject,
        // Notes dual-write: older drafts may have the comment only in
        // `text` (order_notes); mirror it into project_description so the
        // Notes section and status emails can show it.
        projectDescription:
          restUploadProject.projectDescription || restUploadProject.text || "",
        order_id: projectId,
        address: formattedAddress,
        documents: safeDocuments,
        document_cnt: uploadProject.documents?.length || 0,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        pageCount: safeTotalPage,
        wordsCount: safeTotalWords,
        price: calculatedPrice,
        include_expedited_turnaround: includeExpedited,
        transitionSpeed: translateSpeed,
        desktopPublishing: !!(uploadProject as any)?.include_desktop_publishing,
        proofreading: !!(uploadProject as any)?.include_proofreading,
        orderStatus: 10, // Keep as Draft
        wizardStep: currentStep,
        order_due_date: calculateDueDate(uploadProject),
      };

      const save = axiosService.post(`/projects/${user.user_id}`, payload);
      trackDraftSave(save);
      await save;
    } catch (e: any) {
      console.error("Failed to save draft progress:", e);
    } finally {
      isSavingDraftRef.current = false;
    }
  }, [draftProjectId, orderId, uploadProject, user]);

  // Restore draftProjectId from localStorage on mount
  useEffect(() => {
    if (!user?.user_id) return;
    if (orderId) return;
    
    const savedDraftId = localStorage.getItem(getLS_DRAFT_PROJECT_ID(user.user_id));
    if (savedDraftId && !draftProjectId) {
      dispatch(saveDraftProjectId(savedDraftId));
    }
  }, [dispatch, draftProjectId, orderId, user?.user_id]);

  const hasStep2DocumentsError = useCallback(() => {
    const totalDocs = ((uploadProject?.documents as any[]) || []).length || 0;
    const source = String(uploadProject?.sourceLanguage || "").trim();
    const target = String(uploadProject?.targetLanguage?.[0] || "").trim();
    
    // Check if documents are uploaded
    if (totalDocs === 0) return true;
    
    // Check if languages are selected
    if (!source || !target) return true;
    
    // Check if languages are the same
    if (source === target) return true;
    
    // Check if one language is English (language pair validation)
    const hasEnglish = source === "English" || target === "English";
    if (!hasEnglish) return true;
    
    return false;
  }, [uploadProject?.documents, uploadProject?.sourceLanguage, uploadProject?.targetLanguage]);

  useEffect(() => {
    if (isExistingOrder && !isDraftProject) return;
    if (!user?.user_id) return;

    setMaxStep((prev) => {
      // Don't allow maxStep to go beyond 2 if step 2 requirements aren't met
      if (step > 2 && hasStep2DocumentsError()) {
        const cappedNext = Math.min(2, Math.max(prev, step)) as WizardStep;
        localStorage.setItem(getLS_MAX_STEP(user.user_id), String(cappedNext));
        return cappedNext;
      }
      
      const next = (Math.max(prev, step) as unknown) as WizardStep;
      localStorage.setItem(getLS_MAX_STEP(user.user_id), String(next));
      return next;
    });
    localStorage.setItem(getLS_STEP(user.user_id), String(step));
  }, [step, user?.user_id, isExistingOrder, isDraftProject, hasStep2DocumentsError]);

  const goTo = useCallback((target: WizardStep) => {
    if (target > maxStep) return;
    // Prevent navigation from step 2 if documents/languages are not valid
    if (step === 2 && target > 2 && hasStep2DocumentsError()) return;
    // Prevent navigation to step 3+ if step 2 requirements are not met
    if (target > 2 && hasStep2DocumentsError()) return;
    setStep(target);
  }, [maxStep, step, hasStep2DocumentsError]);

  const next = useCallback(() => {
    setStep((prev) => {
      if (prev === 2 && hasStep2DocumentsError()) return prev;
      return prev < 4 ? ((prev + 1) as WizardStep) : prev;
    });
  }, [hasStep2DocumentsError]);

  const back = useCallback(() => {
    setStep((prev) => {
      const nextStep = prev > 1 ? ((prev - 1) as WizardStep) : prev;
      
      // Only clear documents when going back from step 3 to 2 for NEW orders (no existing orderId)
      if (prev === 3 && nextStep === 2 && !orderId) {
        dispatch(updateProjectStatus({ 
          documents: [],
          totalPage: 0,
          totalWords: 0
        }));
      }
      
      return nextStep;
    });
  }, [dispatch, orderId]);

  // ── Auto-save to server on ANY data or step change (debounced 800ms) ──
  const hydrateSkipCountRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Skip renders during and right after hydration to avoid saving stale/initial data back
    if (isHydrating) {
      hydrateSkipCountRef.current = 0;
      return;
    }
    // Skip the first 3 renders after hydration (clearProjectRequest + updateProjectStatus + derivation)
    if (hydrateSkipCountRef.current < 3) {
      hydrateSkipCountRef.current += 1;
      return;
    }

    const projectId = orderId || draftProjectId;
    if (!projectId) return;
    if (!user?.user_id) return;

    // For existing orders, always save changes regardless of data state
    // For new orders, skip saving if state has been cleared (e.g. after successful payment)
    if (!orderId) {
      const hasMeaningfulData = (uploadProject.documents?.length > 0) ||
        uploadProject.projectTitle ||
        uploadProject.text ||
        (uploadProject.targetLanguage?.length > 0);
      if (!hasMeaningfulData) {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        return;
      }
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      saveDraftProgressToServer(step);
    }, 800);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [uploadProject, orderId, draftProjectId, user?.user_id, isHydrating, saveDraftProgressToServer, step]);

  // ── Additional auto-save trigger specifically for existing orders ──
  useEffect(() => {
    if (!orderId) return; // Only for existing orders
    if (!user?.user_id) return;
    if (isHydrating) return;

    // Debounced save for existing orders on any uploadProject change
    const saveTimer = setTimeout(() => {
      saveDraftProgressToServer(step);
    }, 1000);

    return () => clearTimeout(saveTimer);
  }, [uploadProject, orderId, user?.user_id, isHydrating, saveDraftProgressToServer, step]);

  const documentsSummary = useMemo(() => {
    // Exclude documents the backend couldn't auto-parse — their pages/words
    // are 0 by convention (see _Documents::uploadFileByCDN) and treating
    // them as 0 would silently underbill. OrderSummary surfaces the
    // manual-review disclaimer for these so a specialist sets the final
    // price post-checkout.
    const docs = (uploadProject.documents || []).filter(
      (d: any) => d?.parserSuccess !== false
    );
    const pages = docs.reduce((acc: number, d: any) => acc + Number(d.pages || 0), 0);
    const words = docs.reduce((acc: number, d: any) => acc + Number(d.words || 0), 0);
    return { pages, words };
  }, [uploadProject.documents]);

  useEffect(() => {
    // Skip derivation during and right after hydration — server already provided correct totals
    if (isHydrating) return;
    if (justHydratedRef.current) {
      justHydratedRef.current = false;
      return;
    }

    const derivedTotalPage =
      uploadProject.transitionType === "standard"
        ? Math.ceil(Number(documentsSummary.words || 0) / 250)
        : documentsSummary.pages;

    if (derivedTotalPage !== uploadProject.totalPage || documentsSummary.words !== uploadProject.totalWords) {
      dispatch(updateProjectStatus({
        totalPage: derivedTotalPage,
        totalWords: documentsSummary.words,
      }));
    }
  }, [
    dispatch,
    documentsSummary.pages,
    documentsSummary.words,
    uploadProject.totalPage,
    uploadProject.totalWords,
    uploadProject.transitionType,
  ]);

  return {
    step,
    maxStep,
    isHydrating,
    goTo,
    next,
    back,
  };
}
